// DATA-78 frontend follow-up — Cross-org GitHub status table.
// Surfaces /api/cross-org-status: latest snapshot per DCVFD repo.

import { useEffect, useState } from "react";
import { useRealtimeCursor } from "../hooks/useRealtimeCursor";
import PageHeader from "../components/shared/PageHeader";
import Banner from "../components/shared/Banner";

interface RepoRow {
  repo: string;
  default_branch: string | null;
  open_issues: number;
  open_prs: number;
  pushed_at: string | null;
  failing_workflow_runs_24h: number;
  latest_release_tag: string | null;
  snapshot_at: string;
  error_message: string | null;
}

interface Totals {
  open_issues: number;
  open_prs: number;
  failing_runs_24h: number;
}

interface Response {
  repos: RepoRow[];
  totals: Totals;
}

export default function CrossOrgStatus() {
  const [data, setData] = useState<Response | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { updatedAt } = useRealtimeCursor("cross_org");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/cross-org-status", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`/api/cross-org-status → ${r.status}`);
        return (await r.json()) as Response;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [updatedAt]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <PageHeader
          title="Cross-org Status"
          subtitle="Daily snapshot of GitHub state across the DCVFD engineering footprint."
        />
      </div>

      {err && (
        <Banner variant="error" className="mb-4">
          {err}
        </Banner>
      )}

      {data && (
        <>
          <section className="grid grid-cols-3 gap-3 mb-6">
            <Tile label="Open issues" value={data.totals.open_issues} />
            <Tile label="Open PRs" value={data.totals.open_prs} />
            <Tile
              label="Failed runs 24h"
              value={data.totals.failing_runs_24h}
            />
          </section>

          <section className="glass rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Per-repository GitHub status: open issues, open PRs, failed
                  workflows in 24h, latest release, and last push
                </caption>
                <thead>
                  <tr className="text-xs text-theme-muted uppercase tracking-wide border-b border-theme-subtle">
                    <th
                      scope="col"
                      className="text-left py-2.5 px-4 font-medium"
                    >
                      Repo
                    </th>
                    <th
                      scope="col"
                      className="text-right py-2.5 px-4 font-medium"
                    >
                      Issues
                    </th>
                    <th
                      scope="col"
                      className="text-right py-2.5 px-4 font-medium"
                    >
                      PRs
                    </th>
                    <th
                      scope="col"
                      className="text-right py-2.5 px-4 font-medium"
                    >
                      Failed 24h
                    </th>
                    <th
                      scope="col"
                      className="text-left py-2.5 px-4 font-medium"
                    >
                      Latest release
                    </th>
                    <th
                      scope="col"
                      className="text-left py-2.5 px-4 font-medium"
                    >
                      Last push
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.repos.map((r) => (
                    <tr
                      key={r.repo}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      <td className="py-2.5 px-4 font-mono text-theme-primary">
                        <a
                          href={`https://github.com/Dale-City-Volunteer-Fire-Department/${r.repo}`}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="hover:underline"
                        >
                          {r.repo}
                        </a>
                        {r.error_message && (
                          <span
                            className="ml-2 text-xs text-red-400"
                            title={r.error_message}
                          >
                            ⚠
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-right tabular-nums">
                        {r.open_issues}
                      </td>
                      <td className="py-2.5 px-4 text-right tabular-nums">
                        {r.open_prs}
                      </td>
                      <td
                        className={`py-2.5 px-4 text-right tabular-nums ${r.failing_workflow_runs_24h > 0 ? "text-red-400 font-semibold" : ""}`}
                      >
                        {r.failing_workflow_runs_24h}
                      </td>
                      <td className="py-2.5 px-4 text-theme-secondary font-mono text-xs">
                        {r.latest_release_tag ?? "—"}
                      </td>
                      <td className="py-2.5 px-4 text-theme-muted text-xs">
                        {r.pushed_at
                          ? new Date(r.pushed_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  {data.repos.length === 0 && (
                    <tr>
                      <td
                        className="py-4 px-4 text-sm text-theme-muted"
                        colSpan={6}
                      >
                        No snapshot rows yet — the daily cron runs once per UTC
                        day.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-theme-subtle p-3 bg-theme-overlay/40">
      <p className="text-xs uppercase tracking-wider text-theme-muted">
        {label}
      </p>
      <p
        className={`text-2xl font-semibold mt-1 font-mono ${value > 0 && label.startsWith("Failed") ? "text-red-400" : "text-theme-primary"}`}
      >
        {value}
      </p>
    </div>
  );
}
